/**
 * Detects a permission-denied refusal on a Kanban board read, and surfaces
 * its message (SE-8534).
 * @module features/player-kanban/utils/kanbanAccessDenial
 */

/** Minimal shape of the GraphQL errors Apollo attaches to a query error. */
interface GraphQLErrorLike {
  readonly message?: unknown;
  readonly extensions?: { readonly code?: unknown };
}

/**
 * The board-permission guard's extension code. Matched first because it is
 * the contract; the message is only a fallback for a transport that dropped
 * extensions.
 */
const FORBIDDEN_CODE = "FORBIDDEN";

/**
 * The exact refusal `backend-v2`'s `KanbanService` throws
 * (`getUserPermissionForBoard` returning no grant — SE-7918). Matched
 * case-insensitively so a copy tweak upstream does not silently stop being
 * recognized.
 */
const BOARD_PERMISSION_MESSAGE = /don'?t have permission to view this board/i;

/**
 * Reads every GraphQL error off an Apollo-style error, from both the places
 * they can hide.
 *
 * `graphQLErrors` covers a 200 response carrying errors. The `networkError.
 * result.errors` path matters because Apollo Client 3 classifies any non-2xx
 * response as a `ServerError`, and the backend's real message is then only
 * reachable through `result` — the top-level message degrades to "Response
 * not successful: Received status code 4xx". Same reasoning as
 * `resolveScenarioAccessDenial` in squad-planning, which this mirrors.
 * @param error - The value the query failed with.
 * @returns Every GraphQL error found, in the order encountered.
 */
function collectGraphQLErrors(error: unknown): readonly GraphQLErrorLike[] {
  if (typeof error !== "object" || error === null) return [];

  const direct = (error as { graphQLErrors?: unknown }).graphQLErrors;
  const fromTopLevel: readonly GraphQLErrorLike[] = Array.isArray(direct)
    ? (direct as readonly GraphQLErrorLike[])
    : [];

  const networkError = (error as { networkError?: unknown }).networkError;
  const result =
    typeof networkError === "object" && networkError !== null
      ? (networkError as { result?: unknown }).result
      : undefined;
  const nested =
    typeof result === "object" && result !== null
      ? (result as { errors?: unknown }).errors
      : undefined;
  const fromNetwork: readonly GraphQLErrorLike[] = Array.isArray(nested)
    ? (nested as readonly GraphQLErrorLike[])
    : [];

  return [...fromTopLevel, ...fromNetwork];
}

/**
 * Returns the backend's own denial message when a Kanban board read was
 * REFUSED for lack of a `user_kanban_board` grant, else null.
 *
 * Why this exists (SE-8534): a board a viewer has never been granted access
 * to is a correct, permanent 403 — not a transient failure worth retrying.
 * `useKanbanBoardQueries` used to treat every failed read identically,
 * including feeding a permission-denied `getKanbanBoard: null` into the
 * SE-6491 replica-lag retry, which re-issued the same denied read up to 4
 * times automatically. Callers use this to both skip that retry and render
 * the server's own wording instead of a generic "please try again" that
 * invites the user to keep re-triggering a request that can never succeed.
 * @param error - The error Apollo reported for a Kanban board/cards/tags
 * query.
 * @returns The refusal message, or null when this is not a permission
 * failure.
 */
export function resolveKanbanAccessDenial(error: unknown): string | null {
  for (const entry of collectGraphQLErrors(error)) {
    const message =
      typeof entry?.message === "string" ? entry.message.trim() : "";
    const isForbidden =
      entry?.extensions?.code === FORBIDDEN_CODE ||
      BOARD_PERMISSION_MESSAGE.test(message);
    if (isForbidden && message.length > 0) return message;
  }
  return null;
}
